import React, { useEffect, useState, useMemo } from 'react';
import {
  Alert,
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Chip,
  Checkbox,
  FormControlLabel,
  ThemeProvider,
  CssBaseline,
  Link,
  Button
} from '@mui/material';
import { StylesProvider } from '@mui/styles';
import {
  Search as SearchIcon,
  CalendarToday as CalendarIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { getSiteTheme } from '../theme/siteTheme';
import { useDocumentThemeSync } from '../theme/useDocumentThemeSync';

const PAGE_SIZE = 36;

const NewsCatalog = () => {
  const [selectedTag, setSelectedTag] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [displayLimit, setDisplayLimit] = useState(PAGE_SIZE);
  const [historyMode, setHistoryMode] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const isDarkMode = useDocumentThemeSync();

  // Safely access window.newsData and provide default values
  const defaultNewsData = typeof window !== 'undefined' ? window.newsData || {} : {};
  const activeNewsData = historyMode && historyData ? historyData : defaultNewsData;
  const newsItems = Array.isArray(activeNewsData.news) ? activeNewsData.news : [];

  // Extract unique tags with safe check
  const tags = useMemo(() => {
    const uniqueTags = [...new Set(newsItems.flatMap(item => item.tags || []))];
    return ['all', ...uniqueTags].filter(Boolean);
  }, [newsItems]);

  // Use shared site theme
  const theme = useMemo(() => getSiteTheme(isDarkMode), [isDarkMode]);

  useEffect(() => {
    setDisplayLimit(PAGE_SIZE);
  }, [searchQuery, selectedTag]);

  const filteredNews = newsItems.filter(item => {
    const matchesTag = selectedTag === 'all' || (item.tags && item.tags.includes(selectedTag));
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch =
      (item.title && item.title.toLowerCase().includes(searchLower)) ||
      (item.content && item.content.toLowerCase().includes(searchLower));
    return matchesTag && matchesSearch;
  });
  const displayedNews = filteredNews.slice(0, displayLimit);
  const remainingNewsCount = Math.max(filteredNews.length - displayedNews.length, 0);
  const handleHistoryToggle = async (event) => {
    if (!event.target.checked) {
      setHistoryMode(false);
      setHistoryError(null);
      setDisplayLimit(PAGE_SIZE);
      return;
    }

    if (historyData) {
      setHistoryMode(true);
      setHistoryError(null);
      setDisplayLimit(PAGE_SIZE);
      return;
    }

    setHistoryLoading(true);
    setHistoryError(null);

    try {
      const res = await fetch('/assets/js/news-data.json');
      if (!res.ok) {
        throw new Error(`History fetch failed: ${res.status}`);
      }

      const data = await res.json();
      if (!Array.isArray(data.news)) {
        throw new Error('History payload missing news array');
      }

      setHistoryData(data);
      setHistoryMode(true);
      setDisplayLimit(PAGE_SIZE);
    } catch (error) {
      setHistoryMode(false);
      setHistoryError('History unavailable.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleRetryHistory = () => {
    handleHistoryToggle({ target: { checked: true } });
  };

  const formatDate = (dateString) => {
    if (typeof dateString !== 'string' || dateString.trim() === '') {
      return 'Date unavailable';
    }

    const dateOnlyMatch = dateString.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      const localDate = new Date(Number(year), Number(month) - 1, Number(day));
      if (
        localDate.getFullYear() !== Number(year) ||
        localDate.getMonth() !== Number(month) - 1 ||
        localDate.getDate() !== Number(day)
      ) {
        return 'Date unavailable';
      }
      return localDate.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }

    // Safari requires ISO 8601 format: "YYYY-MM-DDTHH:MM:SS±HH:MM" (no space before timezone)
    // Convert "YYYY-MM-DD HH:MM:SS ±HHMM" to "YYYY-MM-DDTHH:MM:SS±HH:MM"
    const isoString = dateString
      .replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}) ([+-]\d{2})(\d{2})$/, '$1T$2$3:$4')
      .replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2}) ([+-]\d{2}:\d{2})$/, '$1T$2$3');

    const date = new Date(isoString);

    // Handle invalid dates gracefully
    if (isNaN(date.getTime())) {
      console.error('Invalid date:', dateString);
      console.error('Converted to:', isoString);
      return 'Date unavailable';
    }

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getExcerpt = (content, maxLength = 200) => {
    if (!content) return '';
    const text = content.replace(/<[^>]*>/g, '').replace(/#{1,6}\s/g, '');
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  return (
    <StylesProvider injectFirst>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Container
          maxWidth="xl"
          disableGutters
          sx={{
            py: { xs: 4, md: 6 },
            px: { xs: 0, sm: 2, md: 3 },
            mx: 'auto',
            transition: 'background-color 0.3s ease',
            width: '100%',
            '@media (min-width: 1600px)': {
              maxWidth: '1480px',
            },
          }}
        >
          {/* Search and Filter */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 2, md: 1.5 },
              alignItems: { md: 'center' },
              mb: { xs: 3, md: 4 },
            }}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search news & insights..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
              }}
            />
            <TextField
              select
              value={selectedTag}
              onChange={(e) => setSelectedTag(e.target.value)}
              variant="outlined"
              sx={{ minWidth: 180, flexShrink: 0 }}
            >
              {tags.map((tag) => (
                <MenuItem key={tag} value={tag}>
                  {tag === 'all' ? 'All Topics' : tag}
                </MenuItem>
              ))}
            </TextField>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                flex: '0 0 auto',
                alignSelf: { md: 'center' },
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={historyMode}
                    disabled={historyLoading}
                    onChange={handleHistoryToggle}
                    size="small"
                  />
                }
                label="Historic"
                sx={{
                  m: 0,
                  whiteSpace: 'nowrap',
                  color: 'text.primary',
                  width: 'auto',
                  '& .MuiFormControlLabel-label': {
                    fontSize: '0.8rem',
                  },
                  '& .MuiCheckbox-root': {
                    p: 0.5,
                  },
                }}
              />
              {historyLoading && (
                <Typography variant="caption" color="text.secondary">
                  Loading history...
                </Typography>
              )}
            </Box>
          </Box>

          {historyError && (
            <Alert
              severity="warning"
              sx={{ mb: { xs: 2, md: 3 }, py: 0 }}
              action={
                <Button color="inherit" size="small" onClick={handleRetryHistory}>
                  Retry
                </Button>
              }
            >
              {historyError}
            </Alert>
          )}

          {/* News Grid */}
          <Grid
            container
            columnSpacing={{ xs: 0, sm: 2, lg: 3 }}
            rowSpacing={{ xs: 3, md: 4 }}
          >
            {displayedNews.map((item) => (
              <Grid item xs={12} sm={6} md={6} lg={6} xl={4} key={item.slug}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderRadius: 2,
                    boxShadow: isDarkMode
                      ? '0 8px 20px rgba(0, 0, 0, 0.35)'
                      : '0 8px 24px rgba(47, 46, 42, 0.12)',
                    borderColor: isDarkMode
                      ? 'rgba(255,255,255,0.08)'
                      : 'rgba(47,46,42,0.08)',
                    borderWidth: 1,
                    borderStyle: 'solid',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: isDarkMode
                        ? '0 12px 28px rgba(0, 0, 0, 0.45)'
                        : '0 12px 32px rgba(47, 46, 42, 0.16)',
                    },
                    backgroundImage: 'none',
                  }}
                >
                  <CardContent
                    sx={{
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {/* Date Badge */}
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        icon={<CalendarIcon />}
                        label={formatDate(item.date)}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                      {item.automated && (
                        <Chip
                          label="AI-Powered"
                          size="small"
                          color="info"
                          variant="outlined"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>

                    {/* Title */}
                    <Link
                      href={item.url}
                      underline="hover"
                      color="inherit"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'flex-start',
                        textUnderlineOffset: '0.2em',
                        transition: 'color 0.2s ease',
                        '&:hover, &:focus-visible': {
                          color: isDarkMode ? '#f0f6fc' : '#212529',
                          '& .news-title': {
                            fontWeight: 700,
                          },
                        },
                      }}
                    >
                      <Typography
                        variant="h6"
                        component="h2"
                        className="news-title"
                        sx={{
                          mb: 2,
                          transition: 'font-weight 0.2s',
                        }}
                      >
                        {item.title}
                      </Typography>
                    </Link>

                    {/* Excerpt */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2, flexGrow: 1 }}
                    >
                      {getExcerpt(item.content)}
                    </Typography>

                    {/* Tags */}
                    {item.tags && item.tags.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {item.tags.map((tag, index) => (
                            <Chip
                              key={index}
                              label={tag}
                              size="small"
                              variant="outlined"
                              onClick={() => setSelectedTag(tag)}
                              sx={{
                                cursor: 'pointer',
                                px: 1.5,
                                borderRadius: '999px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                fontWeight: 600,
                                fontSize: '0.65rem',
                                color: 'text.primary',
                                borderColor: 'divider',
                                backgroundColor: isDarkMode ? 'rgba(240,246,252,0.08)' : 'rgba(33,37,41,0.05)',
                                '&:hover': {
                                  borderColor: 'text.primary',
                                  backgroundColor: isDarkMode ? 'rgba(240,246,252,0.16)' : 'rgba(33,37,41,0.08)',
                                },
                                '&:focus-visible': {
                                  outline: '2px solid',
                                  outlineColor: isDarkMode ? '#f0f6fc' : '#212529',
                                  outlineOffset: '2px',
                                },
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Read More Button */}
                    <Box sx={{ mt: 'auto', pt: 2 }}>
                      <Button
                        variant="contained"
                        size="small"
                        href={item.url}
                        endIcon={<OpenInNewIcon />}
                        fullWidth
                      >
                        Read Insights
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {remainingNewsCount > 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, md: 5 } }}>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => setDisplayLimit((currentLimit) => currentLimit + PAGE_SIZE)}
                sx={{
                  borderColor: 'divider',
                  color: 'text.primary',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    borderColor: 'text.primary',
                    backgroundColor: isDarkMode ? 'rgba(240,246,252,0.08)' : 'rgba(33,37,41,0.04)',
                  },
                }}
              >
                Load More ({remainingNewsCount} remaining)
              </Button>
            </Box>
          )}

          {/* No Results Message */}
          {filteredNews.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary">
                No news reports found matching your criteria
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your search or filter
              </Typography>
            </Box>
          )}
        </Container>
      </ThemeProvider>
    </StylesProvider>
  );
};

export default NewsCatalog;
